import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faXmark } from '@fortawesome/free-solid-svg-icons';
import { AssetUrls } from 'config/assets';
import { PLAY_PRODUCT_NAME } from 'constants/play';
import './PlayLayout.css';

export interface PlayTab {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** Router path for the tab */
  to: string;
  active: boolean;
  /** Red count badge (TO Round tab: open judge calls + disputes) */
  badge?: number;
}

interface Props {
  children: React.ReactNode;
  /** Header title line; omitted on the landing, which shows the product name alone */
  title?: string;
  subtitle?: string;
  /** Renders a back control that navigates to this path, calls the function, or goes back ('back') */
  backTo?: string | 'back' | (() => void);
  /** Chevron (default) or an X for a modal-like step such as New tournament */
  backIcon?: 'chevron' | 'close';
  actions?: React.ReactNode;
  tabs?: PlayTab[];
  /** Desktop frames for the organizer console use the wider column */
  wide?: boolean;
  /**
   * 'step': a full-screen step (decklist submit). 52px header with only the
   * back control and a 16/700 title, no brand, body as a flex column with
   * 4px 20px 16px padding so a pinned footer and a growing textarea fit.
   */
  variant?: 'default' | 'step';
  className?: string;
}

/**
 * The Play shell (Decision 1): PLAY badge header, optional bottom tab bar, no
 * site nav or footer. Sets body.play-page for the dark page background and
 * clears it on unmount so the rest of the site is untouched. With a title the
 * brand collapses to the icon logo (prototype tournament header).
 */
export const PlayLayout: React.FC<Props> = ({ children, title, subtitle, backTo, backIcon = 'chevron', actions, tabs, wide, variant = 'default', className }) => {
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('play-page');
    return () => {
      document.body.classList.remove('play-page');
    };
  }, []);

  const classes = [
    'play-layout',
    wide ? 'play-layout--wide' : '',
    tabs && tabs.length > 0 ? 'play-layout--with-tabbar' : '',
    variant === 'step' ? 'play-layout--step' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const goBack = () => {
    if (typeof backTo === 'function') backTo();
    else if (backTo === 'back') navigate(-1);
    else if (backTo) navigate(backTo);
  };

  return (
    <div className={classes} data-testid="play-layout">
      <header className="play-header">
        <div className="play-header__inner">
          {backTo && (
            <button type="button" className="play-header__back" aria-label={backIcon === 'close' ? 'Close' : 'Back'} onClick={goBack}>
              <FontAwesomeIcon icon={backIcon === 'close' ? faXmark : faChevronLeft} />
            </button>
          )}
          {variant !== 'step' && (
            <Link to="/play" className={`play-header__brand ${title !== undefined ? 'play-header__brand--compact' : ''}`} aria-label={PLAY_PRODUCT_NAME}>
              <img src={AssetUrls.logos.iconLogo} alt="" className="play-header__logo" />
              <span>TradeMagic</span>
              <span className="play-badge">PLAY</span>
            </Link>
          )}
          {title !== undefined && (
            <div className="play-header__title">
              <span className="play-header__title-main">{title}</span>
              {subtitle && <span className="play-header__title-sub">{subtitle}</span>}
            </div>
          )}
          {actions && <div className="play-header__actions">{actions}</div>}
        </div>
      </header>
      <main className="play-main">{children}</main>
      {tabs && tabs.length > 0 && (
        <nav className="play-tabbar" aria-label="Play sections">
          <div className="play-tabbar__inner">
            {tabs.map((tab) => (
              <Link
                key={tab.key}
                to={tab.to}
                className={`play-tab ${tab.active ? 'play-tab--active' : ''}`}
                aria-current={tab.active ? 'page' : undefined}
                data-testid={`play-tab-${tab.key}`}
              >
                <span className="play-tab__icon" aria-hidden="true">
                  {tab.icon}
                </span>
                <span>{tab.label}</span>
                {tab.badge ? (
                  <span className="play-tab__badge" data-testid={`play-tab-badge-${tab.key}`}>
                    {tab.badge}
                  </span>
                ) : null}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
};

export default PlayLayout;
